import React from "react";
import { BrowserRouter, Route, Routes, Navigate } from "react-router-dom";
import Layout from "./Layout";
import Router from "./Router";
import { LoadingProvider, useLoading } from "../context/LoadingProvider";
import { AuthProvider } from "../context/AuthProvider";
import SplashScreen from "../components/loading/SplashScreen";
import RefreshIndicator from "../components/loading/RefreshIndicator";
import PageTitle from "../components/PageTitle";
import Login from "../pages/auth/Login";
import ChangePassword from "../pages/auth/ChangePassword";
import RequireAuth from "../components/RequireAuth";
import PersistLogin from "../components/PersistLogin";
import useAuth from "../hooks/useAuth";

// Runs AFTER PersistLogin has refreshed the token from the cookie (if Remember Me).
// Only then is the auth state trustworthy enough to decide where to send the user.
const RootRedirect = () => {
  const { auth } = useAuth();
  const token = auth?.accessToken || localStorage.getItem("accessToken");
  return token ? (
    <Navigate to="/main/dashboard" replace />
  ) : (
    <Navigate to="/login" replace />
  );
};

function AppContent() {
  const {
    showSplash,
    showRefresh,
    handleSplashComplete,
    handleRefreshComplete,
    isInitializing,
  } = useLoading();

  // Preserve scroll position on refresh
  React.useEffect(() => {
    // Enable browser scroll restoration to keep position
    if ("scrollRestoration" in window.history) {
      window.history.scrollRestoration = "auto";
    }
  }, []);

  return (
    <>
      {/* Splash Screen - Shows on new tab only */}
      {showSplash && <SplashScreen onComplete={handleSplashComplete} />}

      {/* Refresh Indicator - Shows on page refresh */}
      {showRefresh && <RefreshIndicator onComplete={handleRefreshComplete} />}

      {/* Main App - Show after initialization */}
      <div
        style={{
          opacity: isInitializing ? 0 : 1,
          transition: "opacity 0.3s ease",
        }}
      >
        <BrowserRouter>
          <PageTitle />
          <Routes>
            {/*
              PersistLogin wraps everything. When persist=true and no in-memory
              token exists (browser was closed), it calls auth/refresh to get a
              new token from the httpOnly cookie BEFORE any child renders.
              This is what makes "Remember Me" work across browser restarts.
            */}
            <Route element={<PersistLogin />}>
              {/* Smart root: dashboard if logged in, login if not */}
              <Route path="/" element={<RootRedirect />} />

              {/* Public routes */}
              <Route path="/login" element={<Login />} />
              <Route path="/change-password" element={<ChangePassword />} />

              {/* Protected routes */}
              <Route element={<RequireAuth />}>
                <Route element={<Layout />}>
                  <Route path="/*" element={<Router />} />
                </Route>
              </Route>
            </Route>
          </Routes>
        </BrowserRouter>
      </div>
    </>
  );
}

function App() {
  return (
    <AuthProvider>
      <LoadingProvider>
        <AppContent />
      </LoadingProvider>
    </AuthProvider>
  );
}

export default App;
